/** Small DOM helpers. Every value goes in through textContent or attributes, never innerHTML. */

type Child = Node | string | number | null | undefined | false;
type Props = Record<string, string | number | boolean | null | undefined | ((e: Event) => void)>;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props ?? {})) {
    if (v == null || v === false) continue;
    if (typeof v === "function") el.addEventListener(k.replace(/^on/, "").toLowerCase(), v);
    else if (k === "class") el.className = String(v);
    else if (v === true) el.setAttribute(k, "");
    else el.setAttribute(k, String(v));
  }
  append(el, ...children);
  return el;
}

export function append(el: Element, ...children: Child[]) {
  for (const c of children) {
    if (c == null || c === false) continue;
    el.append(typeof c === "string" || typeof c === "number" ? document.createTextNode(String(c)) : c);
  }
}

export function clear(el: Element) {
  while (el.firstChild) el.firstChild.remove();
}

/** A short, local date: "10 Nov". Blank dates read "never". */
export function when(iso: string | null | undefined, none = "never"): string {
  if (!iso) return none;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(+d)) return none;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export function whenTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Today in the browser's zone, YYYY-MM-DD. */
export function todayKey(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export const PERSONA_NAMES: Record<string, string> = {
  ome: "ome",
  tara: "Tara",
  kai: "Kai",
  kai_teams: "Kai for Teams",
  nova: "Nova",
};

/** Words for a seat's allowed buddies. An empty list allows every one. */
export function personasLabel(list: string[] | undefined): string {
  if (!list || list.length === 0) return "All buddies";
  return list.map((p) => PERSONA_NAMES[p] ?? p).join(", ");
}

/** Whether this seat can train with Kai for Teams. */
export function hasTrainingAccess(list: string[] | undefined): boolean {
  return !list || list.length === 0 || list.includes("kai_teams");
}

/**
 * The seat's personas with Kai for Teams turned on or off. Turning it off on an
 * "all buddies" seat keeps every other buddy; on a training-only seat it leaves ome.
 */
export function withTrainingAccess(list: string[] | undefined, on: boolean): string[] {
  const all = Object.keys(PERSONA_NAMES);
  const current = !list || list.length === 0 ? all : list;
  const next = on ? [...new Set([...current, "kai_teams"])] : current.filter((p) => p !== "kai_teams");
  // An empty list means every buddy, so a seat left with none keeps only the plain helper.
  if (next.length === 0) return ["ome"];
  return next.length === all.length ? [] : all.filter((p) => next.includes(p));
}

export type Route =
  | { name: "overview" }
  | { name: "person"; id: string }
  | { name: "people" }
  | { name: "groups" }
  | { name: "tracks" }
  | { name: "activity" };

export function parseRoute(hash: string): Route {
  const [, name = "", id = ""] = hash.replace(/^#/, "").split("/");
  switch (name) {
    case "person":
      return /^[0-9a-f-]{36}$/i.test(id) ? { name: "person", id } : { name: "overview" };
    case "people":
    case "groups":
    case "tracks":
    case "activity":
      return { name };
    default:
      return { name: "overview" };
  }
}

export function go(route: string) {
  location.hash = `#/${route}`;
}

/** A modal question with a clear yes. Resolves false when dismissed. */
export function confirmDialog(title: string, body: string, yes: string, danger = false): Promise<boolean> {
  return new Promise((resolve) => {
    const dlg = h("dialog", { class: "dialog" });
    const done = (v: boolean) => {
      dlg.close();
      dlg.remove();
      resolve(v);
    };
    append(
      dlg,
      h("h2", null, title),
      h("p", null, body),
      h(
        "div",
        { class: "actions" },
        h("button", { class: danger ? "danger" : "primary", type: "button", onclick: () => done(true) }, yes),
        h("button", { type: "button", onclick: () => done(false) }, "Cancel"),
      ),
    );
    dlg.addEventListener("cancel", () => done(false));
    document.body.append(dlg);
    dlg.showModal();
  });
}

let toastTimer: number | undefined;

/** A one-line status at the bottom of the screen. */
export function toast(text: string, kind: "ok" | "error" = "ok") {
  let t = document.getElementById("toast");
  if (!t) {
    t = h("div", { id: "toast", role: "status", "aria-live": "polite" });
    document.body.append(t);
  }
  t.textContent = text;
  t.className = `toast ${kind}`;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t?.classList.add("gone"), 4000);
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong.";
}

/** Runs a button's action with the button disabled; shows the error as a toast. */
export async function busy(btn: HTMLButtonElement, work: () => Promise<void>) {
  btn.disabled = true;
  try {
    await work();
  } catch (e) {
    toast(errorText(e), "error");
  } finally {
    btn.disabled = false;
  }
}

/**
 * The module strip: one stop per module, like stations on a line. Done stops are
 * filled, a started one is ringed, the rest are open.
 */
export function trackLine(modules: { id: string; title: string; status: string; score?: number | null }[]): HTMLElement {
  const line = h("ol", { class: "line", "aria-label": `${modules.filter((m) => m.status === "done").length} of ${modules.length} modules done` });
  for (const m of modules) {
    const label = `${m.id} ${m.title}: ${m.status === "done" ? "done" : m.status === "started" ? "in progress" : "not started"}${m.score != null ? `, scored ${m.score}` : ""}`;
    line.append(h("li", { class: `stop ${m.status}`, title: label }, h("span", { class: "sr" }, label)));
  }
  return line;
}

/** The strip from the overview's module states (no titles there). */
export function stopsLine(stops: string[], trackTitle: string): HTMLElement {
  return trackLine(stops.map((status, i) => ({ id: `m${i + 1}`, title: trackTitle, status })));
}
